import { computed, ref } from 'vue'
import { getWebsiteProductConfig, getWebsiteProducts } from '@/api/trade'

const config = ref({})
const products = ref([])
const selectedProductCode = ref('')
const loading = ref(false)
const loaded = ref(false)
const error = ref('')
let loadPromise = null

function money(value) {
  return `¥${Number(value || 0).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`
}

/**
 * 规范商品包含内容，兼容数组和多行文本。
 * @param product 商品对象
 * @returns {string[]} 包含内容列表
 */
function normalizeIncludes(product) {
  if (Array.isArray(product?.includesList)) {
    return product.includesList.map(item => String(item || '').trim()).filter(Boolean)
  }
  return String(product?.includesText || '').split(/\r?\n/)
    .map(item => item.trim())
    .filter(Boolean)
}

/**
 * 规范商品允许的收款方式。
 * @param methods 收款方式列表
 * @returns {string[]} 收款方式编码列表
 */
export function normalizePaymentMethods(methods) {
  const value = Array.isArray(methods) ? methods : []
  return value.map(item => String(item || '').trim().toUpperCase()).filter(Boolean)
}

/**
 * 转换收款方式展示名。
 * @param method 收款方式编码
 * @returns {string} 展示名
 */
export function paymentMethodText(method) {
  if (method === 'ALIPAY') return '支付宝'
  if (method === 'BANK_TRANSFER') return '转账'
  return method || ''
}

/**
 * 判断是否城市合伙人套餐商品。
 * @param product 商品对象
 * @returns {boolean} 是否套餐商品
 */
export function isCityPartnerProduct(product) {
  return String(product?.productType || '').toUpperCase() === 'CITY_PARTNER_PACKAGE'
    || String(product?.productCode || '').toUpperCase().startsWith('CITY_PARTNER')
}

/**
 * 转换后端商品为前端统一结构。
 * @param product 后端商品
 * @returns {object} 规范化商品
 */
function normalizeProduct(product) {
  return {
    ...product,
    includesList: normalizeIncludes(product),
    cooperationCondition: String(product?.cooperationCondition || '').trim(),
    allowedPaymentMethods: normalizePaymentMethods(
      product?.allowedPaymentMethods?.length ? product.allowedPaymentMethods : product?.paymentMethods
    )
  }
}

/**
 * 根据后端商品编码选择官网展示图。
 * @param product 商品对象
 * @returns {string} 图片地址
 */
export function websiteProductImage(product) {
  const code = String(product?.productCode || '').toUpperCase()
  if (code.includes('GEO')) return '/images/pexels-content-timeline.jpg'
  if (code.includes('CUSTOMER')) return '/images/pexels-growth-dashboard.jpg'
  if (code.includes('CITY_PARTNER')) return '/images/pexels-private-ops-laptop.jpg'
  return '/images/pexels-humanoid-robot.jpg'
}

/**
 * 转换后端商品为 PC 与移动端首页共享的展示结构。
 * @param product 后端商品对象
 * @param priceHidden 是否隐藏价格
 * @returns {object} 首页商品展示对象
 */
export function toWebsiteProductCard(product, priceHidden = true) {
  return {
    ...product,
    title: product?.productName || '官方商品',
    eyebrow: product?.benefitName || product?.productType || 'VitaeAI 商品',
    subtitle: product?.subtitle || '官方配置商品',
    desc: product?.description || '查看商品权益与交付内容。',
    points: product?.includesList || [],
    metrics: [
      { label: '商品形态', value: product?.formText || product?.productType },
      { label: '服务周期', value: product?.servicePeriod },
      { label: '赠送资源', value: product?.giftComputePower }
    ].filter(metric => metric.value),
    delivery: [
      product?.formText,
      product?.servicePeriod
    ].filter(Boolean).join(' · ') || '官方商品',
    priceText: priceHidden ? '价格以确认订单为准' : money(product?.unitPrice),
    image: websiteProductImage(product)
  }
}

/**
 * 读取官网配置与商品目录。
 * @param force 是否强制刷新
 * @returns {Promise<object>} 官网配置
 */
async function loadProductConfig(force = false) {
  if (!force && (loaded.value || loadPromise)) return loadPromise

  loading.value = true
  error.value = ''
  loadPromise = Promise.all([
    getWebsiteProductConfig(),
    getWebsiteProducts()
  ])
    .then(([configResult, productList]) => {
      config.value = configResult || {}
      products.value = (productList || []).map(normalizeProduct)
      if (!selectedProductCode.value && products.value.length) {
        selectedProductCode.value = products.value[0].productCode
      }
      loaded.value = true
      return config.value
    })
    .catch(err => {
      error.value = err?.message || '商品信息加载失败，请稍后重试'
      throw err
    })
    .finally(() => {
      loading.value = false
      loadPromise = null
    })
  return loadPromise
}

export function useWebsiteProductConfig() {
  const selectedProduct = computed(() => products.value.find(
    product => product.productCode === selectedProductCode.value
  ) || null)
  const priceText = computed(() => {
    if (loading.value && !loaded.value) return '获取价格中...'
    if (config.value.priceHidden === true || !selectedProduct.value) return '价格以确认订单为准'
    return money(selectedProduct.value.unitPrice)
  })

  const freightText = computed(() => {
    const freight = Number(selectedProduct.value?.freightAmount || 0)
    return freight > 0 ? `运费 ${money(freight)}` : '免运费'
  })

  const productSummary = computed(() => [
    selectedProduct.value?.subtitle,
    selectedProduct.value?.formText,
    selectedProduct.value?.servicePeriod,
    selectedProduct.value?.giftComputePower
  ].filter(Boolean).join(' · '))

  const productIncludes = computed(() => selectedProduct.value?.includesList || [])

  const productMetrics = computed(() => [
    { label: '商品形态', value: selectedProduct.value?.formText || selectedProduct.value?.productType },
    { label: '服务周期', value: selectedProduct.value?.servicePeriod },
    { label: '赠送资源', value: selectedProduct.value?.giftComputePower }
  ].filter(item => item.value))

  /**
   * 选择当前商品；空编码回退到首个商品，保证无编码详情页有默认商品。
   * @param code 商品编码
   */
  function selectProduct(code) {
    const productCode = String(code || '').trim()
    selectedProductCode.value = productCode || products.value[0]?.productCode || ''
  }

  return {
    productConfig: config,
    websiteProducts: products,
    selectedProduct,
    selectedProductCode,
    productConfigLoading: loading,
    productConfigLoaded: loaded,
    productConfigError: error,
    selectProduct,
    loadProductConfig,
    priceText,
    freightText,
    productSummary,
    productIncludes,
    productMetrics
  }
}
